import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'
import { ArrowLeft, BadgeCheck, FileDown, Undo2 } from 'lucide-react'
import { PAYMENT_EVENT_OUTCOMES, formatAmount, type PaymentEventOutcome } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Empty,
  InfoGrid,
  InfoItem,
  Input,
  MatriculeTag,
  Modal,
  PageHeader,
  StatusBadge,
  Textarea,
  useAuth,
  useToast,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { EVENT_OUTCOME_VARIANT, PAYMENTS_KEY, type AdminPaymentDetail } from '../../lib/payments'

const SOURCE_LABELS = {
  webhook: 'Notification',
  poll: 'Interrogation',
  manual: 'Saisie',
  reconcile: 'Rapprochement',
} as const

/** Détail d'un paiement : rapprochement, événements reçus, remboursement, reçu. */
export function PaymentPage() {
  const { id = '' } = useParams()
  const { session } = useAuth()
  const toast = useToast()
  const [refunding, setRefunding] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const detail = useQuery({
    queryKey: [...PAYMENTS_KEY, id],
    queryFn: () => session.api.get<AdminPaymentDetail>(`/admin/payments/${id}`),
  })
  const receipt = useMutation({
    mutationFn: () => session.api.download(`/admin/payments/${id}/receipt`),
    onSuccess: (file) => {
      const url = URL.createObjectURL(file.blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.filename ?? 'recu.pdf'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  if (detail.isError)
    return (
      <Card>
        <Empty title="Paiement introuvable" description={errorMessage(detail.error)} />
      </Card>
    )
  const d = detail.data
  if (!d) return <div className="skeleton" style={{ height: 320 }} aria-busy="true" />

  return (
    <>
      <div>
        <Link to="/paiements" className="btn btn-ghost btn-sm">
          <ArrowLeft aria-hidden="true" /> Paiements
        </Link>
      </div>
      <PageHeader
        eyebrow={`${d.competition.reference} · ${d.methodLabel}`}
        title={formatAmount(d.total, d.currency)}
        description={`${d.candidate.name} — ${d.candidate.email}`}
        actions={
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            {d.receiptAvailable && (
              <Button
                variant="secondary"
                icon={<FileDown />}
                loading={receipt.isPending}
                onClick={() => receipt.mutate()}
              >
                Reçu
              </Button>
            )}
            {['processing', 'failed', 'cancelled'].includes(d.status) && (
              <Can perm="payment.manual_entry">
                <Button variant="primary" icon={<BadgeCheck />} onClick={() => setConfirming(true)}>
                  Valider le paiement
                </Button>
              </Can>
            )}
            {d.refundable && (
              <Can perm="payment.refund">
                <Button variant="danger" icon={<Undo2 />} onClick={() => setRefunding(true)}>
                  Rembourser
                </Button>
              </Can>
            )}
          </div>
        }
      />
      <div className="stack" style={{ gap: 'var(--space-5)' }}>
        {d.anomaly && (
          <Alert tone="danger" title={`Anomalie : ${d.anomalyLabel}`}>
            La notification ne correspondait pas au paiement attendu : il n’a pas été confirmé
            automatiquement. Vérifiez la transaction chez le prestataire avant toute action
            (remboursement d’un double paiement, saisie manuelle).
          </Alert>
        )}
        <Card>
          <CardBody>
            <InfoGrid>
              <InfoItem label="Statut">
                <StatusBadge domain="payment" status={d.status} />
              </InfoItem>
              <InfoItem label="Référence">
                <span className="mono">{d.reference}</span>
              </InfoItem>
              <InfoItem label="Transaction du prestataire">
                <span className="mono">{d.providerTxnId ?? '—'}</span>
              </InfoItem>
              <InfoItem label="Dossier">
                <Link to={`/candidatures/${d.application.id}`}>
                  <MatriculeTag kind="candidature" value={d.application.number} copyable={false} />
                </Link>
              </InfoItem>
              <InfoItem label="Frais d’inscription">{formatAmount(d.amount, d.currency)}</InfoItem>
              <InfoItem label="Frais du moyen">{formatAmount(d.fees, d.currency)}</InfoItem>
              <InfoItem label="Créé le">{when(d.createdAt)}</InfoItem>
              <InfoItem label="Payé le">{when(d.paidAt)}</InfoItem>
              <InfoItem label="Environnement">
                {d.environment === 'sandbox' ? (
                  <Badge variant="warning">Test (sandbox)</Badge>
                ) : d.environment === 'live' ? (
                  'Production'
                ) : (
                  '—'
                )}
              </InfoItem>
              <InfoItem label="Dernière vérification">{when(d.lastCheckedAt)}</InfoItem>
              <InfoItem label="Rapproché le">{when(d.reconciledAt)}</InfoItem>
              {d.failureMessage && <InfoItem label="Dernier échec">{d.failureMessage}</InfoItem>}
            </InfoGrid>
            {d.manual && (
              <Alert tone="info" title="Saisie manuelle">
                {d.manualReason} — {d.createdBy}
              </Alert>
            )}
            {d.refundedAt && (
              <Alert tone="warning" title={`Remboursé le ${when(d.refundedAt)}`}>
                {d.refundReason} — {d.refundedBy}
              </Alert>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Événements reçus" />
          <CardBody>
            {d.events.length ? (
              <ol className="timeline">
                {d.events.map((e) => (
                  <li key={e.id}>
                    <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      <strong>{SOURCE_LABELS[e.source]}</strong>
                      {e.type && <span className="mono text-muted">{e.type}</span>}
                      {e.outcome && (
                        <Badge variant={EVENT_OUTCOME_VARIANT[e.outcome] ?? 'neutral'} dot>
                          {PAYMENT_EVENT_OUTCOMES[e.outcome as PaymentEventOutcome] ?? e.outcome}
                        </Badge>
                      )}
                      {!e.authentic && <Badge variant="danger">Non authentique</Badge>}
                    </div>
                    <div className="timeline-meta">
                      {when(e.receivedAt)}
                      {e.detail ? ` — ${e.detail}` : ''}
                    </div>
                    {e.payload !== null && (
                      <details>
                        <summary className="text-muted" style={{ fontSize: 'var(--text-sm)' }}>
                          Contenu reçu
                        </summary>
                        <pre className="code-block">{JSON.stringify(e.payload, null, 2)}</pre>
                      </details>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-muted" style={{ margin: 0 }}>
                Aucune notification reçue pour ce paiement.
              </p>
            )}
          </CardBody>
        </Card>
      </div>
      {refunding && <RefundModal detail={d} onClose={() => setRefunding(false)} />}
      {confirming && <ConfirmPaymentModal detail={d} onClose={() => setConfirming(false)} />}
    </>
  )
}

function RefundModal({ detail, onClose }: { detail: AdminPaymentDetail; onClose: () => void }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const refund = useMutation({
    mutationFn: () =>
      session.api.post<{ viaProvider: boolean }>(`/admin/payments/${detail.id}/refund`, {
        reason,
      }),
    onSuccess: async (res) => {
      toast.success(
        res.viaProvider ? 'Remboursement effectué par le prestataire' : 'Remboursement enregistré'
      )
      await queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY })
      onClose()
    },
    onError: (error) => {
      if (!fieldErrors(error).reason) toast.error(errorMessage(error))
    },
  })
  return (
    <Modal
      open
      onClose={onClose}
      title={`Rembourser ${formatAmount(detail.total, detail.currency)}`}
      description={detail.reference}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="danger"
            loading={refund.isPending}
            disabled={reason.trim().length < 10}
            onClick={() => refund.mutate()}
          >
            {detail.refundViaProvider
              ? 'Rembourser via le prestataire'
              : 'Enregistrer le remboursement'}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <Alert tone={detail.refundViaProvider ? 'warning' : 'info'}>
          {detail.refundViaProvider
            ? 'Le remboursement est demandé au prestataire : le candidat est recrédité. Le paiement du dossier redevient « remboursé ».'
            : 'Ce moyen ne permet pas le remboursement automatique : effectuez-le hors plateforme, puis enregistrez-le ici.'}
        </Alert>
        <Textarea
          label="Motif"
          required
          rows={3}
          hint="10 caractères minimum (ex. : double paiement, retrait avant clôture)."
          value={reason}
          error={fieldErrors(refund.error).reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
    </Modal>
  )
}

/**
 * Validation manuelle d'un paiement en ligne (en cours, échoué ou annulé) après
 * vérification chez le prestataire : le statut est d'abord redemandé au prestataire ;
 * sinon le paiement est confirmé avec la référence relevée. Tracé au journal d'audit.
 */
export function ConfirmPaymentModal({
  detail,
  onClose,
}: {
  detail: Pick<AdminPaymentDetail, 'id' | 'reference' | 'total' | 'currency'>
  onClose: () => void
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [providerReference, setProviderReference] = useState('')
  const [paidAt, setPaidAt] = useState('')
  const [reason, setReason] = useState('')
  const confirm = useMutation({
    mutationFn: () =>
      session.api.post<{ viaProvider: boolean }>(`/admin/payments/${detail.id}/confirm`, {
        providerReference,
        reason,
        paidAt: paidAt || undefined,
      }),
    onSuccess: async (res) => {
      toast.success(
        res.viaProvider ? 'Paiement confirmé par le prestataire' : 'Paiement validé',
        'Le dossier est à jour ; le reçu est envoyé au candidat.'
      )
      // Liste, fiche du paiement et fiche du dossier (état du paiement) se rafraîchissent.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'applications'] }),
      ])
      onClose()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(confirm.error)
  return (
    <Modal
      open
      onClose={onClose}
      title={`Valider le paiement de ${formatAmount(detail.total, detail.currency)}`}
      description={detail.reference}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            icon={<BadgeCheck />}
            loading={confirm.isPending}
            disabled={reason.trim().length < 10 || providerReference.trim().length < 3}
            onClick={() => confirm.mutate()}
          >
            Valider le paiement
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <Alert tone="warning">
          À n’utiliser qu’après avoir constaté le paiement chez le prestataire (portail Wave
          Business, Orange Money…) : montant, date et référence. Le statut est d’abord redemandé au
          prestataire ; la validation est tracée au journal d’audit et clôt toute autre tentative en
          cours.
        </Alert>
        <Input
          label="Référence de la transaction chez le prestataire"
          required
          mono
          hint="Identifiant relevé dans le portail du prestataire (ex. T_ABC123)."
          value={providerReference}
          error={errors.providerReference}
          onChange={(e) => setProviderReference(e.target.value)}
        />
        <Input
          label="Date du paiement"
          type="date"
          hint="Facultatif : aujourd’hui par défaut."
          value={paidAt}
          error={errors.paidAt}
          onChange={(e) => setPaidAt(e.target.value)}
        />
        <Textarea
          label="Motif et vérification effectuée"
          required
          rows={3}
          hint="10 caractères minimum (ex. : notification perdue, paiement constaté dans le portail Wave le 03/11)."
          value={reason}
          error={errors.reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
    </Modal>
  )
}
